import type {
  ServiceBusChange,
  ServiceBusChangeValue,
  ServiceBusSubscription,
  ServiceBusTopic,
  TrackedSubscription,
  TrackedTopic,
} from './types';

function topicToValue(name: string): ServiceBusChangeValue {
  return { name };
}

export function subscriptionToChangeValue(
  s: ServiceBusSubscription,
): ServiceBusChangeValue {
  const v: ServiceBusChangeValue = {
    name: s.name,
    topic: s.topic,
    sql_filter: s.sql_filter,
  };
  if (s.lock_duration) {
    v.lock_duration = s.lock_duration;
  }
  if (s.max_delivery_count !== undefined) {
    v.max_delivery_count = s.max_delivery_count;
  }
  if (s.requires_session !== undefined) {
    v.requires_session = s.requires_session;
  }
  return v;
}

function subsEqual(
  a: ServiceBusSubscription,
  b: ServiceBusSubscription,
): boolean {
  return (
    a.name === b.name &&
    a.topic === b.topic &&
    a.sql_filter === b.sql_filter &&
    (a.lock_duration || '') === (b.lock_duration || '') &&
    (a.max_delivery_count ?? undefined) ===
      (b.max_delivery_count ?? undefined) &&
    (a.requires_session ?? false) === (b.requires_session ?? false)
  );
}

/** Parses topic name from an id in the form `topic:<name>`. */
function parseTopicId(id: string): string | null {
  const m = /^topic:(.+)$/.exec(id);
  return m ? m[1] : null;
}

/** Parses topic and subscription name from an id in the form `sub:<topic>/<name>`. */
function parseSubId(id: string): { topic: string; name: string } | null {
  const m = /^sub:([^/]+)\/(.+)$/.exec(id);
  return m ? { topic: m[1], name: m[2] } : null;
}

/**
 * Compares tracked state with the original GitLab snapshot and builds the changeset
 * for the review step and backend.
 *
 * Each tracked item's `id` is the changeset key and carries the item's original
 * identity (for repo-backed items and new items).
 */
export function computeChangeset(
  trackedTopics: TrackedTopic[],
  trackedSubs: TrackedSubscription[],
  originalTopics: ServiceBusTopic[],
  originalSubs: ServiceBusSubscription[],
): ServiceBusChange[] {
  const originalTopicSet = new Set(originalTopics.map(t => t.name));

  const findOriginalSub = (identityTopic: string, identityName: string) =>
    originalSubs.find(
      os => os.topic === identityTopic && os.name === identityName,
    );

  const changes: ServiceBusChange[] = [];

  // --- Topics ---
  for (const t of trackedTopics) {
    if (t.status === 'deleted') {
      const origName = parseTopicId(t.id);
      if (!origName || !originalTopicSet.has(origName)) {
        continue;
      }
      changes.push({
        key: t.id,
        entityType: 'topic',
        action: 'delete',
        previousValue: topicToValue(origName),
      });
      continue;
    }

    if (t.status === 'added') {
      changes.push({
        key: t.id,
        entityType: 'topic',
        action: 'add',
        newValue: topicToValue(t.name),
      });
      continue;
    }

    if (t.status === 'modified') {
      const origName = parseTopicId(t.id);
      if (!origName || origName === t.name) {
        continue;
      }
      changes.push({
        key: t.id,
        entityType: 'topic',
        action: 'modify',
        previousValue: topicToValue(origName),
        newValue: topicToValue(t.name),
      });
    }
  }

  // --- Subscriptions ---
  for (const s of trackedSubs) {
    const parsed = parseSubId(s.id);
    const identityTopic = parsed?.topic;
    const identityName = parsed?.name;

    if (s.status === 'deleted') {
      if (!identityTopic || !identityName) continue;
      const orig = findOriginalSub(identityTopic, identityName);
      if (!orig) {
        continue;
      }
      changes.push({
        key: s.id,
        entityType: 'subscription',
        action: 'delete',
        previousValue: subscriptionToChangeValue(orig),
      });
      continue;
    }

    if (s.status === 'added') {
      changes.push({
        key: s.id,
        entityType: 'subscription',
        action: 'add',
        newValue: subscriptionToChangeValue(s),
      });
      continue;
    }

    if (s.status === 'modified') {
      if (!identityTopic || !identityName) continue;
      const orig = findOriginalSub(identityTopic, identityName);
      if (!orig) {
        continue;
      }
      const cur: ServiceBusSubscription = {
        name: s.name,
        topic: s.topic,
        sql_filter: s.sql_filter,
      };
      if (s.lock_duration) {
        cur.lock_duration = s.lock_duration;
      }
      if (s.max_delivery_count !== undefined) {
        cur.max_delivery_count = s.max_delivery_count;
      }
      if (s.requires_session !== undefined) {
        cur.requires_session = s.requires_session;
      }
      if (subsEqual(orig, cur)) {
        continue;
      }
      changes.push({
        key: s.id,
        entityType: 'subscription',
        action: 'modify',
        previousValue: subscriptionToChangeValue(orig),
        newValue: subscriptionToChangeValue(cur),
      });
    }
  }

  return changes;
}
